import { useState, useEffect, useCallback } from 'react'
import { ConversationService } from '../services/conversations'
import { CategoryService } from '../services/categories'
import { AuthService } from '../services/auth'
import type {
  ConversationWithDetails,
  ConversationStats,
  ConversationPromptSnapshot,
} from '../services/conversations'
import { SchoolService } from '../services/schools'
import type { Category } from '../services/categories'
import type { School } from '../services/schools'
import type { Database } from '../types/database'
import { AI_RESPONSE_POLICY_OPTIONS } from '../constants/aiResponsePolicy'
import { formatNumber } from '../utils/formatNumber'
import { roleLabel, roleColor } from '../utils/roleLabel'

type ConversationMessage = Database['public']['Tables']['conversation_messages']['Row']

// Labels for the 回答方針 an admin sees in AI設定, so a snapshot names the mode rather
// than its storage key. Falls back to the raw key for a value no longer in the list.
const responsePolicyLabel = (key: string | null): string => {
  if (!key) return '適用なし'
  const index = AI_RESPONSE_POLICY_OPTIONS.findIndex((option) => option.value === key)
  return index >= 0 ? `選択肢${index + 1}（${key}）` : key
}

// Same options and page size as 生徒管理 so the two screens behave alike
const GRADES = ['1年', '2年', '3年', '4年', '5年', '6年']
const PAGE_SIZE = 100

// 管理者 covers both admin and super_admin: the distinction does not matter when
// looking for whose conversation this is, so the filter offers one option for the two.
const ROLE_FILTER_OPTIONS = [
  { value: 'student', label: '生徒', roles: ['student'] },
  { value: 'teacher', label: '先生', roles: ['teacher'] },
  { value: 'admin', label: '管理者', roles: ['admin', 'super_admin'] },
]

const ConversationMonitor = () => {
  const [conversations, setConversations] = useState<ConversationWithDetails[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [schools, setSchools] = useState<School[]>([])
  const [stats, setStats] = useState<ConversationStats | null>(null)
  const [selectedConversation, setSelectedConversation] = useState<ConversationWithDetails | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [filters, setFilters] = useState({
    categoryId: '',
    status: '',
    searchTerm: '',
    schoolId: '',
    grade: '',
    class: '',
    role: '',
  })
  const [currentPage, setCurrentPage] = useState(0)
  const [totalCount, setTotalCount] = useState(0)

  // Detail modal contents. Kept separate from the list's own loading/error state so a
  // failure while reading one conversation cannot blank the list behind it.
  const [detail, setDetail] = useState<{
    messages: ConversationMessage[]
    snapshots: ConversationPromptSnapshot[]
    isLoading: boolean
    error: string
  }>({ messages: [], snapshots: [], isLoading: false, error: '' })
  const [expandedSnapshotId, setExpandedSnapshotId] = useState<string | null>(null)

  const loadConversations = useCallback(async () => {
    try {
      setIsLoading(true)
      setError('')
      const { conversations, totalCount } = await ConversationService.getAllConversations(
        PAGE_SIZE,
        currentPage * PAGE_SIZE,
        filters.categoryId || undefined,
        filters.status || undefined,
        {
          search: filters.searchTerm.trim() || undefined,
          schoolId: filters.schoolId || undefined,
          grade: filters.grade || undefined,
          class: filters.class.trim() || undefined,
          roles: ROLE_FILTER_OPTIONS.find((option) => option.value === filters.role)?.roles,
        }
      )
      setConversations(conversations)
      setTotalCount(totalCount)
    } catch (error) {
      console.error('Failed to load conversations:', error)
      setError('会話の読み込みに失敗しました')
    } finally {
      setIsLoading(false)
    }
  }, [
    currentPage,
    filters.categoryId,
    filters.status,
    filters.searchTerm,
    filters.schoolId,
    filters.grade,
    filters.class,
    filters.role,
  ])

  const loadCategories = useCallback(async () => {
    try {
      const categoriesData = await CategoryService.getAllCategories(true)
      setCategories(categoriesData)
    } catch (error) {
      console.error('Failed to load categories:', error)
    }
  }, [])

  const loadSchools = useCallback(async () => {
    try {
      setSchools(await SchoolService.getSchools())
    } catch (error) {
      console.error('Failed to load schools:', error)
    }
  }, [])

  const loadStats = useCallback(async () => {
    try {
      const statsData = await ConversationService.getConversationStats()
      setStats(statsData)
    } catch (error) {
      console.error('Failed to load conversation stats:', error)
    }
  }, [])

  // A narrowed result set can be shorter than the page you were on, so start over
  useEffect(() => {
    setCurrentPage(0)
  }, [filters])

  useEffect(() => {
    loadConversations()
    loadCategories()
    loadSchools()
    loadStats()
  }, [loadConversations, loadCategories, loadSchools, loadStats])

  // The modal scrolls its own body. Without freezing the page behind it, a wheel event
  // that reaches the backdrop scrolls the conversation list instead of the messages.
  useEffect(() => {
    if (!selectedConversation) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [selectedConversation])

  // Load the selected conversation's messages and recorded instructions together: the
  // instructions only mean something next to the turns they produced.
  useEffect(() => {
    const conversationId = selectedConversation?.id
    if (!conversationId) return

    let cancelled = false
    setDetail({ messages: [], snapshots: [], isLoading: true, error: '' })
    setExpandedSnapshotId(null)

    Promise.all([
      ConversationService.getConversationMessages(conversationId, 500),
      ConversationService.getPromptSnapshots(conversationId),
    ])
      .then(([messages, snapshots]) => {
        if (cancelled) return
        setDetail({ messages, snapshots, isLoading: false, error: '' })
        setExpandedSnapshotId(snapshots[0]?.id ?? null)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to load conversation detail:', err)
        setDetail({
          messages: [],
          snapshots: [],
          isLoading: false,
          error: err instanceof Error ? err.message : '会話詳細の読み込みに失敗しました',
        })
      })

    return () => {
      cancelled = true
    }
  }, [selectedConversation?.id])

  // Archiving and deleting change what students see and are not undone from this screen,
  // so both ask first. Restoring is harmless and goes through unprompted.
  const STATUS_CONFIRMATIONS: Partial<Record<'active' | 'archived' | 'deleted', string>> = {
    archived: 'この会話をアーカイブします。よろしいですか？',
    deleted: 'この会話を削除します。よろしいですか？',
  }

  const handleStatusUpdate = async (
    conversation: ConversationWithDetails,
    newStatus: 'active' | 'archived' | 'deleted'
  ) => {
    const confirmation = STATUS_CONFIRMATIONS[newStatus]
    if (confirmation && !window.confirm(`${confirmation}\n\n対象: ${conversation.title}（${conversation.user.name}）`)) {
      return
    }

    const conversationId = conversation.id
    try {
      await ConversationService.updateConversationStatus(conversationId, newStatus)
      
      await AuthService.logAdminAction('update_conversation_status', 'conversation', conversationId, {
        status: newStatus
      })
      
      await loadConversations()
      await loadStats()
    } catch (error: unknown) {
      console.error('Failed to update conversation status:', error)
      const errorMessage = error instanceof Error ? error.message : String(error)
      setError(errorMessage || '会話ステータスの更新に失敗しました')
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'archived':
        return 'bg-yellow-100 text-yellow-800'
      case 'deleted':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'アクティブ'
      case 'archived':
        return 'アーカイブ済み'
      case 'deleted':
        return '削除済み'
      default:
        return status
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-medium text-gray-900">チャット監視</h2>
          <p className="text-sm text-gray-600">ユーザーとAIの会話履歴と統計情報を監視できます</p>
        </div>
      </div>

      {/* Stats Dashboard */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-blue-50 p-6 rounded-lg">
            <h3 className="text-sm font-medium text-blue-900">総会話数</h3>
            <p className="text-2xl font-bold text-blue-900">{stats.totalConversations}</p>
            <p className="text-xs text-blue-700 mt-1">
              アクティブ: {stats.activeConversations}
            </p>
          </div>
          <div className="bg-green-50 p-6 rounded-lg">
            <h3 className="text-sm font-medium text-green-900">総メッセージ数</h3>
            <p className="text-2xl font-bold text-green-900">{stats.totalMessages}</p>
            <p className="text-xs text-green-700 mt-1">
              平均: {stats.averageMessagesPerConversation.toFixed(1)}件/会話
            </p>
          </div>
          <div className="bg-purple-50 p-6 rounded-lg">
            <h3 className="text-sm font-medium text-purple-900">カテゴリー別</h3>
            <div className="mt-2 space-y-1">
              {stats.conversationsByCategory.slice(0, 3).map((cat) => (
                <div key={cat.category_name} className="flex justify-between text-xs">
                  <span className="text-purple-700">{cat.category_name}</span>
                  <span className="text-purple-900 font-medium">{cat.count}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-orange-50 p-6 rounded-lg">
            <h3 className="text-sm font-medium text-orange-900">ステータス別</h3>
            <div className="mt-2 space-y-1">
              {stats.conversationsByStatus.map((status) => (
                <div key={status.status} className="flex justify-between text-xs">
                  <span className="text-orange-700">{getStatusLabel(status.status)}</span>
                  <span className="text-orange-900 font-medium">{status.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              検索
            </label>
            <input
              type="text"
              value={filters.searchTerm}
              onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
              placeholder="名前・メールアドレス"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              学校
            </label>
            <select
              value={filters.schoolId}
              onChange={(e) => setFilters({ ...filters, schoolId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">全て</option>
              {schools.map((school) => (
                <option key={school.id} value={school.id}>{school.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              学年
            </label>
            <select
              value={filters.grade}
              onChange={(e) => setFilters({ ...filters, grade: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">全て</option>
              {GRADES.map((grade) => (
                <option key={grade} value={grade}>{grade}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              クラス
            </label>
            <input
              type="text"
              value={filters.class}
              onChange={(e) => setFilters({ ...filters, class: e.target.value })}
              placeholder="クラス名で検索"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              役割
            </label>
            <select
              value={filters.role}
              onChange={(e) => setFilters({ ...filters, role: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">全て（生徒・先生・管理者）</option>
              {ROLE_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              カテゴリー
            </label>
            <select
              value={filters.categoryId}
              onChange={(e) => setFilters({ ...filters, categoryId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">全てのカテゴリー</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ステータス
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">全てのステータス</option>
              <option value="active">アクティブ</option>
              <option value="archived">アーカイブ済み</option>
              <option value="deleted">削除済み</option>
            </select>
          </div>
        </div>
        {(filters.schoolId || filters.grade || filters.class) && (
          <p className="mt-3 text-xs text-amber-700">
            学校・学年・クラスは生徒にのみ設定されているため、これらで絞り込むと先生と管理者は表示されません。
          </p>
        )}
      </div>

      {/* Conversations List */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-medium text-gray-900">
              会話一覧 ({formatNumber(totalCount)}件)
            </h3>
            {totalCount > PAGE_SIZE && (
              <div className="flex items-center space-x-2 text-sm">
                <span className="text-gray-500">
                  {currentPage * PAGE_SIZE + 1}〜{Math.min((currentPage + 1) * PAGE_SIZE, totalCount)}件
                </span>
                <button
                  onClick={() => setCurrentPage((p) => p - 1)}
                  disabled={currentPage === 0}
                  className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
                >
                  ←前
                </button>
                <button
                  onClick={() => setCurrentPage((p) => p + 1)}
                  disabled={(currentPage + 1) * PAGE_SIZE >= totalCount}
                  className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
                >
                  次→
                </button>
              </div>
            )}
          </div>
        </div>
        
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-600">読み込み中...</p>
          </div>
        ) : conversations.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {conversations.map((conversation) => (
              <div
                key={conversation.id}
                onClick={() => setSelectedConversation(conversation)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setSelectedConversation(conversation)
                  }
                }}
                role="button"
                tabIndex={0}
                className="p-6 hover:bg-gray-50 cursor-pointer focus:outline-none focus:bg-gray-50"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mb-2">
                      <h4 className="text-base font-medium text-gray-900">
                        {conversation.title}
                      </h4>
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {conversation.chat_prompt.category?.name || '汎用チャット'}
                      </span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(conversation.status)}`}>
                        {getStatusLabel(conversation.status)}
                      </span>
                    </div>
                    <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 mb-2">
                      <span className="flex items-center gap-2">
                        ユーザー: {conversation.user.name}
                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${roleColor(conversation.user.role)}`}>
                          {roleLabel(conversation.user.role)}
                        </span>
                      </span>
                      <span>メール: {conversation.user.email}</span>
                      <span>メッセージ数: {conversation.message_count}</span>
                    </div>
                    <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                      {/* Only students carry these; a teacher or admin simply shows none */}
                      {conversation.user.school?.name && <span>学校: {conversation.user.school.name}</span>}
                      {conversation.user.grade && <span>学年: {conversation.user.grade}</span>}
                      {conversation.user.class && <span>クラス: {conversation.user.class}</span>}
                      <span>作成日: {formatDate(conversation.created_at)}</span>
                      {conversation.last_message_at && (
                        <span>最終メッセージ: {formatDate(conversation.last_message_at)}</span>
                      )}
                    </div>
                  </div>
                  {/* The row itself opens the detail, so the buttons must not bubble */}
                  <div className="flex space-x-2 ml-4" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => setSelectedConversation(conversation)}
                      className="text-blue-600 hover:text-blue-500 text-sm font-medium"
                    >
                      詳細
                    </button>
                    {conversation.status === 'active' && (
                      <button
                        onClick={() => handleStatusUpdate(conversation, 'archived')}
                        className="text-yellow-600 hover:text-yellow-500 text-sm font-medium"
                      >
                        アーカイブ
                      </button>
                    )}
                    {conversation.status === 'archived' && (
                      <button
                        onClick={() => handleStatusUpdate(conversation, 'active')}
                        className="text-green-600 hover:text-green-500 text-sm font-medium"
                      >
                        復元
                      </button>
                    )}
                    {conversation.status !== 'deleted' && (
                      <button
                        onClick={() => handleStatusUpdate(conversation, 'deleted')}
                        className="text-red-600 hover:text-red-500 text-sm font-medium"
                      >
                        削除
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <p className="text-gray-500">会話がありません</p>
            <p className="text-sm text-gray-400 mt-1">
              ユーザーがAIチャットを開始すると、ここに表示されます
            </p>
          </div>
        )}
      </div>

      {/* Conversation Detail Modal */}
      {selectedConversation && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedConversation(null)}
        >
          {/* Column layout with a single scrolling body: the header stays put and the
              wheel acts on the messages, not on the list behind the backdrop. */}
          <div
            className="bg-white rounded-lg max-w-4xl w-full max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center shrink-0">
              <h3 className="text-lg font-medium text-gray-900">
                会話詳細: {selectedConversation.title}
              </h3>
              <button
                onClick={() => setSelectedConversation(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <div className="p-6 flex-1 overflow-y-auto overscroll-contain">
              <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                <div>
                  <strong>ユーザー:</strong> {selectedConversation.user.name}
                  <span className={`ml-2 inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${roleColor(selectedConversation.user.role)}`}>
                    {roleLabel(selectedConversation.user.role)}
                  </span>
                </div>
                <div>
                  <strong>メール:</strong> {selectedConversation.user.email}
                </div>
                <div>
                  <strong>学校:</strong> {selectedConversation.user.school?.name || '—'}
                </div>
                <div>
                  <strong>学年・クラス:</strong>{' '}
                  {[selectedConversation.user.grade, selectedConversation.user.class]
                    .filter(Boolean)
                    .join(' ') || '—'}
                </div>
                <div>
                  <strong>カテゴリー:</strong> {selectedConversation.chat_prompt.category?.name || '汎用チャット'}
                </div>
                <div>
                  <strong>みらいワーク:</strong> {selectedConversation.chat_prompt.title}
                </div>
                <div>
                  <strong>ステータス:</strong> 
                  <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedConversation.status)}`}>
                    {getStatusLabel(selectedConversation.status)}
                  </span>
                </div>
                <div>
                  <strong>メッセージ数:</strong> {selectedConversation.message_count}
                </div>
              </div>
              {detail.isLoading && (
                <div className="p-6 flex justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                </div>
              )}

              {!detail.isLoading && detail.error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                  {detail.error}
                </div>
              )}

              {!detail.isLoading && !detail.error && (
                <div className="space-y-6">
                  {/* 送信された instructions */}
                  <section>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">
                      AIに送信されたプロンプト（instructions）
                    </h4>

                    {detail.snapshots.length === 0 ? (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
                        記録がありません。この会話は記録機能を追加する前のものです。
                      </div>
                    ) : (
                      <>
                        <p className="text-xs text-gray-500 mb-2">
                          この会話では {detail.snapshots.length} 種類の instructions が使われました。
                          {detail.snapshots.length > 1 && '（会話の途中で設定が変更されています）'}
                        </p>
                        <div className="space-y-2">
                          {detail.snapshots.map((snapshot) => {
                            const isOpen = expandedSnapshotId === snapshot.id
                            return (
                              <div key={snapshot.id} className="border border-gray-200 rounded-lg">
                                <button
                                  onClick={() => setExpandedSnapshotId(isOpen ? null : snapshot.id)}
                                  className="w-full text-left px-4 py-3 hover:bg-gray-50"
                                >
                                  <div className="flex justify-between items-start gap-3">
                                    <div className="text-xs text-gray-700 space-y-1">
                                      <div className="font-medium text-gray-900">
                                        回答方針: {responsePolicyLabel(snapshot.applied_response_policy)}
                                      </div>
                                      <div>
                                        学校: {snapshot.school?.name || '未選択'} ／ モデル: {snapshot.model || '不明'}
                                      </div>
                                      <div>
                                        推論: {snapshot.reasoning_effort || 'モデル既定'} ／ 詳しさ:{' '}
                                        {snapshot.text_verbosity || 'モデル既定'} ／ 最大出力:{' '}
                                        {snapshot.max_output_tokens ?? '—'}
                                        {snapshot.used_previous_response_id && ' ／ 履歴引き継ぎあり'}
                                      </div>
                                      <div className="text-gray-500">
                                        初回記録: {formatDate(snapshot.created_at)} ／ {snapshot.instructions.length.toLocaleString()} 文字
                                      </div>
                                    </div>
                                    <span className="text-gray-400 text-sm shrink-0">{isOpen ? '▲' : '▼'}</span>
                                  </div>
                                </button>
                                {/* No height cap and no scroller of its own: the modal
                                    body is the only thing that scrolls, so the wheel
                                    never lands on a nested area. */}
                                {isOpen && (
                                  <pre className="border-t border-gray-200 bg-gray-50 p-4 text-xs text-gray-800 whitespace-pre-wrap break-words">
                                    {snapshot.instructions}
                                  </pre>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      </>
                    )}
                  </section>

                  {/* メッセージ履歴 */}
                  <section>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">メッセージ履歴</h4>
                    {detail.messages.length === 0 ? (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
                        メッセージがありません。
                      </div>
                    ) : (
                      // No scroll container of its own: the modal body is the only
                      // scroller, so the wheel never lands on a nested one.
                      <div className="space-y-3">
                        {detail.messages.map((message) => (
                          <div
                            key={message.id}
                            className={message.role === 'user' ? 'flex justify-end' : 'flex justify-start'}
                          >
                            <div
                              className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                                message.role === 'user'
                                  ? 'bg-blue-50 text-blue-900'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {message.content}
                              <div className="mt-1 text-[10px] text-gray-500">
                                {formatDate(message.created_at)}
                                {message.model_used && ` ／ ${message.model_used}`}
                                {message.tokens_used != null && ` ／ ${message.tokens_used.toLocaleString()} tokens`}
                                {message.openai_response_id && ` ／ ${message.openai_response_id}`}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
    </div>
  )
}

export default ConversationMonitor